import { useEffect, useState } from 'react';
import { AppLink } from '@/components/ui/app-link';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { Content, DocumentDetail, TemplateContent } from '@vivport/backend/types';
import { getContent, downloadDocumentExport, listContent, type DocumentExportFormat } from '@/api/content-api';
import { assertToolSucceeded } from '@/api/templates-api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ContentDescriptionEditable } from '@/components/widget/content-description-editable';
import { NewFromTemplateDialog } from '@/components/explore/new-from-template-dialog';
import { useNavigateWithContentFlush } from '@/hooks';
import {
  useDiscardTemplateDraft,
  usePublishTemplate,
  useReinstateTemplate,
  useRetireTemplate,
  useTemplateRead,
  useTemplatesList,
  useUpdateTemplate,
} from '@/hooks/useTemplateQueries';
import { toContentHref } from '@/utils/content-ref';
import { DetailsDocument } from './DetailsDocument';
import { TemplateResultsTab } from './template-chrome/TemplateResultsTab';
import { ExportMenu } from './DetailsContentActionButtons';
import { FileText, Play } from 'lucide-react';

interface DetailsTemplateProps {
  content: TemplateContent;
  onDescriptionSave: (description: string) => Promise<void>;
}

const STATUS_LABELS = { DRAFT: 'Draft', PUBLISHED: 'Published', RETIRED: 'Retired' } as const;

function surfaceError(error: unknown): void {
  toast.error(error instanceof Error ? error.message : 'The action failed.');
}

export function DetailsTemplate({ content, onDescriptionSave }: DetailsTemplateProps) {
  const navigate = useNavigateWithContentFlush();
  const [whenToUseDraft, setWhenToUseDraft] = useState(content.whenToUse);
  const [startDialogOpen, setStartDialogOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    setWhenToUseDraft(content.whenToUse);
  }, [content.whenToUse]);

  const templateRead = useTemplateRead({ templateCode: content.code });
  const templatesList = useTemplatesList();
  const listEntry = templatesList.data?.templates.find((entry) => entry.templateCode === content.code);

  const definitionQuery = useQuery({
    queryKey: ['content-ref', content.definitionCode],
    queryFn: () => getContent(content.definitionCode as string),
    enabled: content.definitionCode !== null,
  });
  const definitionDetail =
    definitionQuery.data?.contentType === 'DOCUMENT' ? (definitionQuery.data as DocumentDetail) : null;

  const instancesQuery = useQuery({
    queryKey: ['content', 'template-instances', content.id],
    queryFn: () =>
      listContent({
        templateId: content.id,
        contentTypes: ['DOCUMENT'],
        orderBy: 'createdAt',
        orderDir: 'desc',
        limit: 50,
      }),
    enabled: content.status !== 'DRAFT',
  });

  const updateTemplate = useUpdateTemplate();
  const publishTemplate = usePublishTemplate();
  const retireTemplate = useRetireTemplate();
  const reinstateTemplate = useReinstateTemplate();
  const discardDraft = useDiscardTemplateDraft();
  const busy =
    updateTemplate.isPending ||
    publishTemplate.isPending ||
    retireTemplate.isPending ||
    reinstateTemplate.isPending ||
    discardDraft.isPending;

  const saveWhenToUse = async () => {
    if (whenToUseDraft.trim() === content.whenToUse) return;
    try {
      assertToolSucceeded(
        await updateTemplate.mutateAsync({ templateId: content.id, whenToUse: whenToUseDraft.trim() })
      );
      toast.success('When-to-use updated.');
    } catch (error) {
      surfaceError(error);
    }
  };

  const setCaptureFlow = async (captureFlow: 'EVIDENCE_FIRST' | 'IMMEDIATE') => {
    if (content.captureFlow === captureFlow) return;
    try {
      assertToolSucceeded(await updateTemplate.mutateAsync({ templateId: content.id, captureFlow }));
    } catch (error) {
      surfaceError(error);
    }
  };

  const setLayout = async (layout: 'sectioned' | 'letter') => {
    if (templateRead.data?.layout === layout) return;
    try {
      assertToolSucceeded(await updateTemplate.mutateAsync({ templateId: content.id, layout }));
    } catch (error) {
      surfaceError(error);
    }
  };

  const handlePublish = async () => {
    try {
      assertToolSucceeded(await publishTemplate.mutateAsync({ templateId: content.id }));
      toast.success('Template published — it is now hub knowledge in every conversation.');
    } catch (error) {
      surfaceError(error);
    }
  };

  const handleRetire = async () => {
    if (!window.confirm(`Retire '${content.title}'? Existing instances stay; new ones are refused.`)) {
      return;
    }
    try {
      const result = await retireTemplate.mutateAsync({ templateId: content.id });
      if (result.status === 'PENDING_APPROVAL') {
        toast.info('Retirement queued for approval in Mission Control.');
        return;
      }
      assertToolSucceeded(result);
      toast.success('Template retired.');
    } catch (error) {
      surfaceError(error);
    }
  };

  const handleReinstate = async () => {
    try {
      assertToolSucceeded(await reinstateTemplate.mutateAsync({ templateId: content.id }));
      toast.success('Template reinstated.');
    } catch (error) {
      surfaceError(error);
    }
  };

  const handleDiscard = async () => {
    if (!window.confirm(`Discard the draft '${content.title}'? This deletes the definition and cannot be undone.`)) {
      return;
    }
    try {
      const result = await discardDraft.mutateAsync({ templateId: content.id });
      if (result.status === 'PENDING_APPROVAL') {
        toast.info('Discard queued for approval in Mission Control.');
        return;
      }
      assertToolSucceeded(result);
      toast.success('Draft discarded.');
      navigate('/explore');
    } catch (error) {
      surfaceError(error);
    }
  };

  const handleExport = async (format: DocumentExportFormat) => {
    if (definitionDetail === null) return;
    setIsExporting(true);
    try {
      await downloadDocumentExport(definitionDetail.id, format, content.title ?? undefined);
    } catch (error) {
      surfaceError(error);
    } finally {
      setIsExporting(false);
    }
  };

  const status = content.status;
  const layout = templateRead.data?.layout;

  return (
    <div data-testid="details-template" className="space-y-6">
      <div className="space-y-4 rounded-md border p-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={status === 'PUBLISHED' ? 'default' : 'secondary'}>
            {STATUS_LABELS[status]}
          </Badge>
          {templateRead.data !== undefined ? (
            <span className="text-sm text-muted-foreground">
              {templateRead.data.sections.length} section(s)
              {listEntry !== undefined ? ` · ${listEntry.instanceCount} instance(s)` : ''}
            </span>
          ) : null}
          {content.sourceFileCode !== null ? (
            <AppLink to={toContentHref(content.sourceFileCode)}>
              <Badge variant="outline" className="gap-1">
                <FileText className="h-3 w-3" /> Source file
              </Badge>
            </AppLink>
          ) : null}
          <div className="flex items-center gap-2 text-sm">
            <Label htmlFor="template-capture-flow" className="text-xs uppercase text-muted-foreground">
              Capture flow
            </Label>
            <Switch
              id="template-capture-flow"
              checked={content.captureFlow === 'EVIDENCE_FIRST'}
              disabled={busy || status === 'RETIRED'}
              onCheckedChange={(checked) => void setCaptureFlow(checked ? 'EVIDENCE_FIRST' : 'IMMEDIATE')}
            />
            <span className="text-muted-foreground">
              {content.captureFlow === 'EVIDENCE_FIRST' ? 'Evidence first' : 'Immediate'}
            </span>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {status === 'PUBLISHED' ? (
              <Button size="sm" onClick={() => setStartDialogOpen(true)}>
                <Play className="mr-1.5 h-4 w-4" /> Start new
              </Button>
            ) : null}
            {status === 'DRAFT' ? (
              <Button size="sm" onClick={() => void handlePublish()} disabled={busy}>
                Publish
              </Button>
            ) : null}
            {status === 'DRAFT' ? (
              <Button size="sm" variant="destructive" onClick={() => void handleDiscard()} disabled={busy}>
                Discard
              </Button>
            ) : null}
            {status === 'PUBLISHED' ? (
              <Button size="sm" variant="outline" onClick={() => void handleRetire()} disabled={busy}>
                Retire
              </Button>
            ) : null}
            {status === 'RETIRED' ? (
              <Button size="sm" onClick={() => void handleReinstate()} disabled={busy}>
                Reinstate
              </Button>
            ) : null}
            <ExportMenu isExporting={isExporting} handleExport={(format) => void handleExport(format)} />
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs uppercase text-muted-foreground">Purpose</Label>
          <ContentDescriptionEditable
            description={content.description || ''}
            onSave={onDescriptionSave}
            placeholder="Click to describe what this template produces"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="template-when-to-use" className="text-xs uppercase text-muted-foreground">
            When to use
          </Label>
          <div className="flex items-center gap-2">
            <Input
              id="template-when-to-use"
              value={whenToUseDraft}
              onChange={(event) => setWhenToUseDraft(event.target.value)}
              onBlur={() => void saveWhenToUse()}
              disabled={busy || status === 'RETIRED'}
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs uppercase text-muted-foreground">Export layout</Label>
          <div className="flex overflow-hidden rounded-md border">
            <Button
              size="sm"
              variant={layout === 'sectioned' ? 'default' : 'ghost'}
              className="rounded-none"
              disabled={busy || status === 'RETIRED' || layout === undefined}
              onClick={() => void setLayout('sectioned')}
            >
              Sectioned
            </Button>
            <Button
              size="sm"
              variant={layout === 'letter' ? 'default' : 'ghost'}
              className="rounded-none"
              disabled={busy || status === 'RETIRED' || layout === undefined}
              onClick={() => void setLayout('letter')}
            >
              Letter
            </Button>
          </div>
        </div>
      </div>

      {definitionDetail !== null ? (
        <DetailsDocument content={definitionDetail} isRegenerating={false} />
      ) : content.definitionCode === null ? (
        <p className="text-sm text-muted-foreground">This template has no definition document yet.</p>
      ) : definitionQuery.isError ? (
        <p className="text-sm text-destructive">
          {definitionQuery.error instanceof Error
            ? definitionQuery.error.message
            : 'The definition could not be loaded.'}
        </p>
      ) : (
        <p className="text-sm text-muted-foreground">Loading the definition…</p>
      )}

      {status !== 'DRAFT' ? (
        <Tabs defaultValue="instances" className="rounded-md border p-4">
          <TabsList>
            <TabsTrigger value="instances">Instances</TabsTrigger>
            <TabsTrigger value="results">Results</TabsTrigger>
          </TabsList>
          <TabsContent value="instances" className="space-y-3">
          {instancesQuery.isError ? (
            <p className="text-sm text-destructive">
              {instancesQuery.error instanceof Error
                ? instancesQuery.error.message
                : 'The instances could not be loaded.'}
            </p>
          ) : instancesQuery.data === undefined ? (
            <p className="text-sm text-muted-foreground">Loading instances…</p>
          ) : instancesQuery.data.items.length === 0 ? (
            <p className="text-sm text-muted-foreground">No instances yet.</p>
          ) : (
            <ul className="divide-y">
              {instancesQuery.data.items
                .filter((item): item is Content => !('projectionType' in item))
                .map((item) => (
                  <li key={item.id} className="flex items-center justify-between gap-4 py-2">
                    <AppLink to={toContentHref(item.code)} className="text-sm text-primary hover:underline">
                      {item.title ?? item.code}
                    </AppLink>
                    <span className="text-xs text-muted-foreground">
                      {new Date(item.originCreatedAt).toLocaleDateString()}
                    </span>
                  </li>
                ))}
            </ul>
          )}
          </TabsContent>
          <TabsContent value="results">
            <TemplateResultsTab templateId={content.id} templateName={content.title ?? content.code} />
          </TabsContent>
        </Tabs>
      ) : null}

      <NewFromTemplateDialog
        open={startDialogOpen}
        onOpenChange={setStartDialogOpen}
        preselectedTemplateId={content.id}
      />
    </div>
  );
}
